import { DocsView, SchemaView } from 'criteria-docs-ui'
import { Callout, Inset, Pane, Scroll, Text, Typography } from '@criteria-labs/react'
import { useEffect, useState } from 'react'
import { Document } from '../documents/Document'
import {
  JSONSchemaDocument,
  useJSONSchemaDocumentJSONSchema,
  useJSONSchemaDocumentJSONSchemaWarnings
} from '../documents/JSONSchemaDocument'
import { MarkdownDocument } from '../documents/MarkdownDocument'
import {
  OpenAPIDocument,
  useOpenAPIDocumentOpenAPI,
  useOpenAPIDocumentOpenAPIWarnings
} from '../documents/OpenAPIDocument'
import { ErrorBoundary } from './ErrorBoundary'
import { MarkdownDocumentPreview } from './MarkdownDocumentPreview'

function OpenAPIDocumentPreview({ document }: { document: OpenAPIDocument }) {
  const openAPI = useOpenAPIDocumentOpenAPI(document)
  const openAPIWarnings = useOpenAPIDocumentOpenAPIWarnings(document)

  // filter out undefined values as text is written to avoid flickering
  const [lastOpenAPI, setLastOpenAPI] = useState(openAPI)
  useEffect(() => {
    if (openAPI) {
      setLastOpenAPI(openAPI)
    }
  }, [openAPI])

  return (
    <ErrorBoundary>
      <Scroll>
        <Inset>
          <Typography>
            {openAPIWarnings.length > 0 && (
              <Callout color="red">
                <ul>
                  {openAPIWarnings.map((warning, index) => (
                    <li key={index} className="">
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}
            {lastOpenAPI !== undefined && <DocsView api={lastOpenAPI} location={undefined} />}
          </Typography>
        </Inset>
      </Scroll>
    </ErrorBoundary>
  )
}

function JSONSchemaDocumentPreview({ document }: { document: JSONSchemaDocument }) {
  const jsonSchema = useJSONSchemaDocumentJSONSchema(document)
  const jsonSchemaWarnings = useJSONSchemaDocumentJSONSchemaWarnings(document)

  // filter out undefined values as text is written to avoid flickering
  const [lastJSONSchema, setLastJSONSchema] = useState(jsonSchema)
  useEffect(() => {
    if (jsonSchema) {
      setLastJSONSchema(jsonSchema)
    }
  }, [jsonSchema])

  return (
    <ErrorBoundary>
      <Scroll>
        <Inset>
          <Typography>
            {jsonSchemaWarnings.length > 0 && (
              <Callout color="red">
                <ul>
                  {jsonSchemaWarnings.map((warning, index) => (
                    <li key={index} className="">
                      {warning.message}
                    </li>
                  ))}
                </ul>
              </Callout>
            )}
            {lastJSONSchema !== undefined && <SchemaView schema={lastJSONSchema as any} page />}
          </Typography>
        </Inset>
      </Scroll>
    </ErrorBoundary>
  )
}

export function DocumentPreview({ document }: { document: Document }) {
  if (document instanceof OpenAPIDocument) {
    return <OpenAPIDocumentPreview document={document} />
  } else if (document instanceof JSONSchemaDocument) {
    return <JSONSchemaDocumentPreview document={document} />
  } else if (document instanceof MarkdownDocument) {
    return <MarkdownDocumentPreview document={document} />
  } else {
    return (
      <Pane>
        <Inset className="flex h-full w-full flex-col items-center justify-center">
          <Text className="text-center">No preview available.</Text>
        </Inset>
      </Pane>
    )
  }
}
